<template>
  <div class="pass-tab">

    <!-- Active session takes over the whole tab -->
    <ActiveSession
      v-if="sessionStore.active"
      :exercise-types="exerciseTypes"
      :body-weight-kg="bodyWeightKg"
      @completed="onSessionEnded"
      @discarded="onSessionEnded"
      @toast="(p) => $emit('toast', p)"
    />

    <template v-else>
      <!-- Start -->
      <div class="start-card">
        <h3>Starta ett pass</h3>
        <button class="btn-start-empty" @click="startEmpty" :disabled="starting">
          ⚡ Starta tomt pass
        </button>
        <p class="hint">Tomt pass börjar direkt och en timer startar. Du lägger till övningar allt eftersom.</p>
      </div>

      <!-- Templates -->
      <div class="section">
        <div class="section-head">
          <h3>Mallar</h3>
          <button class="btn-mini" @click="openNewTemplate">+ Ny mall</button>
        </div>
        <div v-if="templates.length === 0" class="empty">
          Inga mallar än. Skapa en återanvändbar rutin, eller spara ett avslutat pass som mall.
        </div>
        <div v-for="t in templates" :key="t.id" class="template-card">
          <div class="template-main">
            <strong>{{ t.name }}</strong>
            <span class="template-meta">{{ (t.exercises || []).length }} övningar<span v-if="t.notes"> · {{ t.notes }}</span></span>
          </div>
          <div class="template-btns">
            <button class="btn-start-tmpl" @click="startFromTemplate(t)" :disabled="starting">Starta</button>
            <button class="btn-icon" @click="openEditTemplate(t)" title="Redigera">✏</button>
            <button class="btn-icon danger" @click="deleteTemplate(t)" title="Ta bort">🗑</button>
          </div>
        </div>
      </div>

      <!-- History -->
      <div class="section">
        <div class="section-head">
          <h3>Tidigare pass</h3>
        </div>
        <div v-if="history.length === 0" class="empty">Inga avslutade pass än.</div>
        <div v-for="s in history" :key="s.id" class="history-card">
          <div class="history-head" @click="toggle(s.id)">
            <div class="history-main">
              <strong>{{ s.name }}</strong>
              <span class="history-meta">{{ formatDate(s.date) }}</span>
            </div>
            <div class="history-stats">
              <span v-if="duration(s) != null">⏱ {{ formatDurationLong(duration(s) * 60) }}</span>
              <span v-if="s.estimatedCalories">🔥 {{ Math.round(s.estimatedCalories) }} kcal</span>
              <span>📋 {{ setCount(s) }} set</span>
            </div>
            <span class="chevron" :class="{ open: expanded[s.id] }">⌄</span>
          </div>
          <div v-if="expanded[s.id]" class="history-detail">
            <div v-for="ex in orderedExercises(s)" :key="ex.id" class="hd-ex">
              <span class="hd-ex-name">{{ ex.exerciseType.name }}</span>
              <span class="hd-ex-sets">
                <button v-for="set in orderedSets(ex)" :key="set.id" class="hd-set" :class="set.status" @click.stop="openSetStatus(ex, set)" :title="setTitle(set)">
                  {{ setSummary(set, ex.exerciseType) }}<span v-if="set.status === 'competition'"> 🎖️</span><span v-else-if="set.status === 'excluded'"> ⛔</span>
                </button>
                <span v-if="!(ex.sets || []).length">–</span>
              </span>
            </div>
            <div class="history-detail-btns">
              <button class="btn-mini" @click="saveAsTemplate(s)">Spara som mall</button>
              <button class="btn-mini danger" @click="deleteSession(s)">Ta bort pass</button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <TemplateEditorModal
      v-if="editor.open"
      :template="editor.template"
      :exercise-types="exerciseTypes"
      @save="saveTemplate"
      @cancel="editor.open = false"
    />
    <LiftStatusSheet v-if="statusSheet" :context="statusSheet" @close="statusSheet = null" @changed="$emit('lifts-changed')" @set-updated="onSetStatusUpdated" />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';
import { sessionStore, loadActiveSession, setActiveSession } from '../../services/gymSessionStore.js';
import { formatDurationLong, setSummary } from '../../services/gymHelpers.js';
import ActiveSession from './ActiveSession.vue';
import LiftStatusSheet from './LiftStatusSheet.vue';
import TemplateEditorModal from './TemplateEditorModal.vue';

export default {
  name: 'GymSessionTab',
  components: { ActiveSession, TemplateEditorModal, LiftStatusSheet },
  props: {
    exerciseTypes: { type: Array, default: () => [] },
    bodyWeightKg: { type: Number, default: null },
  },
  emits: ['toast', 'refresh', 'lifts-changed'],
  setup(props, { emit }) {
    const templates = ref([]);
    const history = ref([]);
    const expanded = reactive({});
    const starting = ref(false);
    const editor = reactive({ open: false, template: null });

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }
    function toast(msg, error = false) { emit('toast', { msg, error }); }

    async function loadTemplates() {
      try {
        const d = await withToken(t => api.getTemplates(t));
        templates.value = d.workoutTemplates || [];
      } catch (e) { toast(`Kunde inte hämta mallar: ${e.message}`, true); }
    }
    async function loadHistory() {
      try {
        const d = await withToken(t => api.getSessions('completed', 30, 0, t));
        history.value = d.gymSessions || [];
      } catch (e) { toast(`Kunde inte hämta historik: ${e.message}`, true); }
    }

    onMounted(async () => {
      if (sessionStore.active === null) await loadActiveSession();
      await Promise.all([loadTemplates(), loadHistory()]);
    });

    async function startEmpty() {
      starting.value = true;
      try {
        const d = await withToken(t => api.startSession({}, t));
        setActiveSession(d.startGymSession);
      } catch (e) { toast(`Kunde inte starta pass: ${e.message}`, true); }
      finally { starting.value = false; }
    }
    async function startFromTemplate(t) {
      starting.value = true;
      try {
        const d = await withToken(tok => api.startSession({ templateId: t.id, name: t.name }, tok));
        setActiveSession(d.startGymSession);
      } catch (e) { toast(`Kunde inte starta från mall: ${e.message}`, true); }
      finally { starting.value = false; }
    }

    function onSessionEnded() {
      loadHistory();
      emit('refresh');   // tell parent to reload lifts / PB / stats
    }

    // Templates CRUD
    function openNewTemplate() { editor.template = null; editor.open = true; }
    function openEditTemplate(t) { editor.template = t; editor.open = true; }
    async function saveTemplate(payload) {
      try {
        if (payload.id) {
          // UpdateWorkoutTemplateInput includes id.
          await withToken(t => api.updateTemplate(payload, t));
        } else {
          // CreateWorkoutTemplateInput has NO id field — sending id:null makes
          // HotChocolate reject the whole input ("field `id` does not exist").
          const { id, ...createInput } = payload; // eslint-disable-line no-unused-vars
          await withToken(t => api.createTemplate(createInput, t));
        }
        editor.open = false;
        await loadTemplates();
        toast('Mall sparad!');
      } catch (e) { toast(`Kunde inte spara mall: ${e.message}`, true); }
    }
    async function deleteTemplate(t) {
      if (!confirm(`Ta bort mallen "${t.name}"?`)) return;
      try {
        await withToken(tok => api.deleteTemplate(t.id, tok));
        templates.value = templates.value.filter(x => x.id !== t.id);
        toast('Mall borttagen.');
      } catch (e) { toast(`Kunde inte ta bort mall: ${e.message}`, true); }
    }
    async function saveAsTemplate(s) {
      try {
        await withToken(t => api.saveSessionAsTemplate(s.id, s.name, t));
        await loadTemplates();
        toast('Passet sparat som mall!');
      } catch (e) { toast(`Kunde inte spara som mall: ${e.message}`, true); }
    }
    async function deleteSession(s) {
      if (!confirm(`Ta bort passet "${s.name}"? Lyften från passet tas också bort.`)) return;
      try {
        await withToken(t => api.deleteSession(s.id, t));
        history.value = history.value.filter(x => x.id !== s.id);
        emit('refresh');
        toast('Pass borttaget.');
      } catch (e) { toast(`Kunde inte ta bort pass: ${e.message}`, true); }
    }

    // History helpers
    function toggle(id) { expanded[id] = !expanded[id]; }
    function duration(s) {
      if (s.durationSecondsOverride != null) return s.durationSecondsOverride / 60;
      if (s.startTime && s.endTime) return (new Date(s.endTime) - new Date(s.startTime)) / 60000;
      return null;
    }
    function setCount(s) {
      return (s.gymSessionExercises || []).reduce((n, ex) => n + (ex.sets || []).length, 0);
    }
    function orderedExercises(s) {
      return [...(s.gymSessionExercises || [])].sort((a, b) => a.order - b.order);
    }
    // Rekordstatus per set i historiken (öppnar samma ark som PB-listan).
    const statusSheet = ref(null);
    function orderedSets(ex) { return [...(ex.sets || [])].sort((a, b) => a.order - b.order); }
    function openSetStatus(ex, set) {
      statusSheet.value = { setId: set.id, set, exerciseTypeId: ex.exerciseType.id, exerciseTypeName: ex.exerciseType.name, reps: set.reps };
    }
    function onSetStatusUpdated({ status, statusNote }) {
      const set = statusSheet.value?.set;
      if (set) { set.status = status; set.statusNote = statusNote; }
    }
    function setTitle(set) {
      const st = set.status === 'competition' ? 'Tävling' : set.status === 'excluded' ? 'Räknas ej' : 'Träning';
      return [st, set.statusNote, set.notes ? `📝 ${set.notes}` : ''].filter(Boolean).join(' · ') + ' – tryck för att ändra';
    }
    function exerciseSetsSummary(ex) {
      return [...(ex.sets || [])].sort((a, b) => a.order - b.order)
        .map(set => setSummary(set, ex.exerciseType)).join(', ') || '–';
    }
    function formatDate(d) { return new Date(d).toLocaleDateString('sv-SE'); }

    return {
      statusSheet, orderedSets, openSetStatus, onSetStatusUpdated, setTitle,
      sessionStore, templates, history, expanded, starting, editor,
      startEmpty, startFromTemplate, onSessionEnded,
      openNewTemplate, openEditTemplate, saveTemplate, deleteTemplate, saveAsTemplate, deleteSession,
      toggle, duration, setCount, orderedExercises, exerciseSetsSummary, formatDate, formatDurationLong,
    };
  },
};
</script>

<style scoped>
.pass-tab { display: flex; flex-direction: column; gap: 1.25rem; }

.start-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem;
  display: flex; flex-direction: column; gap: 0.6rem; box-shadow: var(--gym-shadow-sm);
}
.start-card h3 { margin: 0; font-size: 1.05rem; color: var(--gym-text); }
.btn-start-empty {
  background: linear-gradient(135deg, var(--gym-accent), var(--gym-accent-active)); color: var(--gym-on-accent); border: none;
  border-radius: var(--gym-radius); padding: 1rem; font-size: 1.05rem; font-weight: 700; cursor: pointer;
  box-shadow: var(--gym-shadow-accent);
  transition: transform var(--gym-dur) var(--gym-ease), filter var(--gym-dur) var(--gym-ease);
}
.btn-start-empty:hover:not(:disabled) { filter: brightness(1.05); }
.btn-start-empty:active:not(:disabled) { transform: translateY(1px) scale(0.99); }
.btn-start-empty:disabled { opacity: 0.6; cursor: not-allowed; }
.hint { font-size: 0.8rem; color: var(--gym-text-muted); margin: 0; }

.section { display: flex; flex-direction: column; gap: 0.6rem; }
.section-head { display: flex; align-items: center; justify-content: space-between; }
.section-head h3 { margin: 0; font-size: 1.05rem; color: var(--gym-text); }
.btn-mini {
  background: var(--gym-accent-soft); border: 1px solid var(--gym-accent-border); color: var(--gym-accent);
  border-radius: var(--gym-radius-sm); padding: 0.45rem 0.8rem; font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-mini:hover { background: color-mix(in srgb, var(--gym-accent) 26%, transparent); }
.btn-mini.danger { background: var(--gym-danger-soft); border-color: var(--gym-danger); color: var(--gym-danger); }
.empty { color: var(--gym-text-faint); font-size: 0.88rem; font-style: italic; }

.template-card, .history-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.9rem;
  box-shadow: var(--gym-shadow-sm); transition: border-color var(--gym-dur) var(--gym-ease);
}
.template-card:hover, .history-card:hover { border-color: var(--gym-border-strong); }
.template-card { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; }
.template-main { display: flex; flex-direction: column; gap: 0.2rem; }
.template-main strong { color: var(--gym-text); }
.template-meta { font-size: 0.78rem; color: var(--gym-text-muted); }
.template-btns { display: flex; align-items: center; gap: 0.35rem; }
.btn-start-tmpl {
  background: var(--gym-accent); border: none; color: var(--gym-on-accent); border-radius: var(--gym-radius-sm);
  padding: 0.5rem 0.9rem; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-start-tmpl:hover:not(:disabled) { background: var(--gym-accent-hover); }
.btn-start-tmpl:disabled { opacity: 0.6; }
.btn-icon {
  background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); color: var(--gym-text-muted);
  width: 34px; height: 34px; cursor: pointer; font-size: 0.85rem;
  transition: background var(--gym-dur) var(--gym-ease);
}
.btn-icon:hover { background: var(--gym-surface-3); color: var(--gym-text); }
.btn-icon.danger:hover { background: var(--gym-danger-soft); color: var(--gym-danger); }

.history-head { display: flex; align-items: center; gap: 0.6rem; cursor: pointer; }
.history-main { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.history-main strong { color: var(--gym-text); font-size: 0.95rem; }
.history-meta { font-size: 0.75rem; color: var(--gym-text-faint); }
.history-stats { display: flex; gap: 0.65rem; margin-left: auto; font-size: 0.78rem; color: var(--gym-text-muted); white-space: nowrap; flex-wrap: wrap; justify-content: flex-end; }
.chevron { color: var(--gym-text-faint); transition: transform 0.25s var(--gym-ease); font-size: 1.1rem; }
.chevron.open { transform: rotate(180deg); }
.history-detail { margin-top: 0.7rem; border-top: 1px solid var(--gym-border); padding-top: 0.6rem; display: flex; flex-direction: column; gap: 0.4rem; }
.hd-ex { display: flex; justify-content: space-between; gap: 0.6rem; font-size: 0.85rem; }
.hd-ex-name { font-weight: 600; color: var(--gym-text); }
.hd-ex-sets { color: var(--gym-text-muted); text-align: right; display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.25rem; }
.hd-set { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.1rem 0.5rem; font-size: 0.76rem; cursor: pointer; white-space: nowrap; }
.hd-set:hover { border-color: var(--gym-accent-border); color: var(--gym-text); }
.hd-set.competition { border-color: var(--gym-warning); color: var(--gym-warning); }
.hd-set.excluded { border-color: var(--gym-danger); color: var(--gym-danger); text-decoration: line-through; }
.history-detail-btns { display: flex; gap: 0.5rem; margin-top: 0.3rem; }
</style>
